// ==================================================================================
// Digital Approval System (DAS) - Requests Report Screen (generic)
// ==================================================================================
// One screen drives all five request-lifecycle reports via the `reportType` prop.
// Uses the app's standard master-list layout (module-master-container +
// ModuleHeader + data-table) so it matches the rest of DAS. Server-side
// pagination + sorting + filtering (never loads the full dataset). The request
// number opens a read-only detail modal; "Total Pending Days" opens the
// per-approver breakdown popup; Export streams a backend-built .xlsx.
// ==================================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import Pagination from 'components/common/Pagination';
import SearchableSelect from 'components/common/SearchableSelect';
import { LoadingOverlay, SkeletonStyles } from '../../DashboardSkeleton';
import { useRequestsReport } from 'hooks/api/das/reports';
import type { ReportFilters } from 'hooks/api/das/reports';
import { useActiveRequestTypes } from 'hooks/api';
import { reportsService } from 'api/services/das/reportsService';
import type { ReportType, RequestReportRow } from 'api/types/das/reports.types';
import PendingDaysBreakdownModal from './PendingDaysBreakdownModal';
import ReportRequestDetailModal from './ReportRequestDetailModal';

interface RequestsReportScreenProps {
  reportType: ReportType;
  title: string;
  description?: string;
}

// Advanced (panel) filters — search is handled separately in the header box.
type AdvancedFilters = Omit<ReportFilters, 'searchTerm'>;

// Statuses offered in the All report's status dropdown.
const ALL_STATUS_OPTIONS = [
  'Draft',
  'Submitted',
  'In Review',
  'Pre Approved',
  'In Consolidation',
  'Approved',
  'Rejected',
  'Returned',
  'Cancelled',
  'On Hold',
];

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const statusColor = (status?: string | null): string => {
  const s = (status || '').toLowerCase();
  if (s.includes('approved')) return '#10b981';
  if (s.includes('rejected')) return '#dc2626';
  if (s.includes('returned')) return '#f59e0b';
  if (s.includes('cancelled')) return '#6b7280';
  return '#2563eb';
};

const formatValue = (row: RequestReportRow): string => {
  if (row.value === null || row.value === undefined) return '—';
  const amount = row.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return row.currencyCode ? `${row.currencyCode} ${amount}` : amount;
};

interface RowContext {
  onOpenRequest: (id: string) => void;
  onOpenBreakdown: (id: string, requestNumber: string) => void;
}

interface ColumnDef {
  label: string;
  sortKey?: string;
  align?: 'left' | 'right';
  nowrap?: boolean;
  render: (row: RequestReportRow, ctx: RowContext) => React.ReactNode;
}

const RequestsReportScreen: React.FC<RequestsReportScreenProps> = ({ reportType, title, description }) => {
  const {
    rows,
    pagination,
    loading,
    error,
    query,
    sortBy,
    sortDescending,
    setPage,
    changePageSize,
    toggleSort,
    applyFilters,
    refetch,
  } = useRequestsReport(reportType);

  const { requestTypes } = useActiveRequestTypes();

  // Header search (debounced) + collapsible advanced-filter panel.
  const [searchInput, setSearchInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [advForm, setAdvForm] = useState<AdvancedFilters>({});
  const [appliedAdv, setAppliedAdv] = useState<AdvancedFilters>({});
  const [exporting, setExporting] = useState(false);
  const firstRun = useRef(true);

  // Detail + breakdown modal state.
  const [detailRequestId, setDetailRequestId] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<{ id: string; requestNumber: string } | null>(null);

  // Debounce header search and merge with applied advanced filters. Skip the
  // very first run so we don't double-fetch on mount (the hook already fetches).
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    const handle = setTimeout(() => {
      applyFilters({ ...appliedAdv, searchTerm: searchInput.trim() || undefined });
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput, appliedAdv, applyFilters]);

  const columns = useMemo<ColumnDef[]>(() => {
    const showPendingCols = reportType === 'All' || reportType === 'Pending';

    const cols: ColumnDef[] = [
      {
        label: 'Req No',
        sortKey: 'RequestNumber',
        nowrap: true,
        render: (r, ctx) => (
          <button type="button" onClick={() => ctx.onOpenRequest(r.requestId)} style={linkButtonStyle}>
            {r.requestNumber}
          </button>
        ),
      },
      { label: 'Created Date', sortKey: 'CreatedDate', nowrap: true, render: (r) => formatDate(r.createdDate) },
      { label: 'Created By', nowrap: true, render: (r) => r.createdByName || '—' },
      { label: 'Request Type', sortKey: 'RequestType', nowrap: true, render: (r) => r.requestTypeName || '—' },
      { label: 'Subject', render: (r) => r.subject || '—' },
      {
        label: 'Status',
        sortKey: 'Status',
        nowrap: true,
        render: (r) => (
          <span
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: 999,
              fontSize: '0.72rem',
              color: '#fff',
              background: statusColor(r.status),
              whiteSpace: 'nowrap',
            }}
          >
            {r.status || '—'}
          </span>
        ),
      },
      { label: 'Value', sortKey: 'Value', align: 'right', nowrap: true, render: (r) => formatValue(r) },
      { label: 'Final Approver', nowrap: true, render: (r) => r.finalApproverName || '—' },
      {
        label: 'Total Pending Days',
        sortKey: 'TotalPendingDays',
        align: 'right',
        nowrap: true,
        render: (r, ctx) => (
          <button
            type="button"
            onClick={() => ctx.onOpenBreakdown(r.requestId, r.requestNumber)}
            style={linkButtonStyle}
          >
            {r.totalPendingDays}
          </button>
        ),
      },
      { label: 'Pending (Last Action)', align: 'right', nowrap: true, render: (r) => r.pendingDaysFromLastAction },
      { label: 'External Ref No', nowrap: true, render: (r) => r.externalReferenceNo || '—' },
    ];

    if (showPendingCols) {
      const idx = cols.findIndex((c) => c.label === 'Final Approver');
      cols.splice(
        idx,
        0,
        { label: 'Pending User', nowrap: true, render: (r) => r.pendingUserName || '—' },
        { label: 'Current Stage', nowrap: true, render: (r) => r.currentStage || '—' }
      );
    }

    if (reportType === 'Approved') {
      cols.push({ label: 'Completed Date', nowrap: true, render: (r) => formatDate(r.completedAt) });
    } else if (reportType === 'Rejected') {
      cols.push({ label: 'Rejected By', nowrap: true, render: (r) => r.lastActionByName || '—' });
      cols.push({ label: 'Rejection Comments', render: (r) => r.lastActionComments || '—' });
    } else if (reportType === 'Returned') {
      cols.push({ label: 'Returned By', nowrap: true, render: (r) => r.lastActionByName || '—' });
      cols.push({ label: 'Returned To', nowrap: true, render: (r) => r.returnedToName || '—' });
      cols.push({ label: 'Return Count', align: 'right', nowrap: true, render: (r) => r.returnCount });
      cols.push({ label: 'Return Reason', render: (r) => r.lastActionComments || '—' });
    } else if (reportType === 'Decided') {
      cols.push({ label: 'Decision Date', nowrap: true, render: (r) => formatDate(r.completedAt || r.lastActionAt) });
      cols.push({ label: 'Decided By', nowrap: true, render: (r) => r.lastActionByName || '—' });
      cols.push({ label: 'Comments', render: (r) => r.lastActionComments || '—' });
    }

    return cols;
  }, [reportType]);

  const rowContext: RowContext = {
    onOpenRequest: (id) => setDetailRequestId(id),
    onOpenBreakdown: (id, requestNumber) => setBreakdown({ id, requestNumber }),
  };

  const handleApplyFilters = () => {
    const cleaned: AdvancedFilters = {
      requestTypeId: advForm.requestTypeId || undefined,
      status: advForm.status || undefined,
      submittedFrom: advForm.submittedFrom || undefined,
      submittedTo: advForm.submittedTo || undefined,
      minValue: advForm.minValue ?? undefined,
      maxValue: advForm.maxValue ?? undefined,
    };
    setAppliedAdv(cleaned); // triggers the debounced effect → applyFilters
    // Keep the panel open on Apply — it closes only via the header ✕ or the funnel toggle.
  };

  const handleClearFilters = () => {
    setAdvForm({});
    setAppliedAdv({});
  };

  // Remove a single applied advanced filter (also clears it in the panel form).
  const removeAdvFilter = useCallback((key: keyof AdvancedFilters) => {
    setAdvForm((f) => {
      const next = { ...f };
      delete next[key];
      return next;
    });
    setAppliedAdv((a) => {
      const next = { ...a };
      delete next[key];
      return next;
    });
  }, []);

  // Clear every filter, including the header search.
  const handleClearAll = useCallback(() => {
    setSearchInput('');
    setAdvForm({});
    setAppliedAdv({});
  }, []);

  const requestTypeName = (id?: string) =>
    requestTypes.find((rt: any) => rt.id === id)?.requestTypeName || id || '';

  // Chips describing every currently-applied filter (search + advanced).
  const activeChips = useMemo(() => {
    const chips: { key: string; text: string; onRemove: () => void }[] = [];
    const search = searchInput.trim();
    if (search) chips.push({ key: 'search', text: `Search: ${search}`, onRemove: () => setSearchInput('') });
    if (appliedAdv.requestTypeId)
      chips.push({ key: 'type', text: `Type: ${requestTypeName(appliedAdv.requestTypeId)}`, onRemove: () => removeAdvFilter('requestTypeId') });
    if (appliedAdv.status)
      chips.push({ key: 'status', text: `Status: ${appliedAdv.status}`, onRemove: () => removeAdvFilter('status') });
    if (appliedAdv.submittedFrom)
      chips.push({ key: 'from', text: `Created From: ${appliedAdv.submittedFrom}`, onRemove: () => removeAdvFilter('submittedFrom') });
    if (appliedAdv.submittedTo)
      chips.push({ key: 'to', text: `Created To: ${appliedAdv.submittedTo}`, onRemove: () => removeAdvFilter('submittedTo') });
    if (appliedAdv.minValue !== undefined)
      chips.push({ key: 'min', text: `Min Value: ${appliedAdv.minValue}`, onRemove: () => removeAdvFilter('minValue') });
    if (appliedAdv.maxValue !== undefined)
      chips.push({ key: 'max', text: `Max Value: ${appliedAdv.maxValue}`, onRemove: () => removeAdvFilter('maxValue') });
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, appliedAdv, requestTypes, removeAdvFilter]);

  const appliedCount = activeChips.length;

  const handleExport = async () => {
    setExporting(true);
    try {
      // Pass the human-readable applied filters so they appear in the Excel banner.
      await reportsService.downloadRequestsReport(
        query,
        activeChips.map((c) => c.text)
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Report export failed', err);
    } finally {
      setExporting(false);
    }
  };

  const actions: ActionButton[] = [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={16} className={loading ? 'spinner' : ''} />,
      label: 'Refresh',
      onClick: refetch,
      tooltip: 'Reload report data',
      variant: 'icon',
      disabled: loading,
      visible: true,
    },
    {
      id: 'export',
      icon: <IconSystem.download size={16} className={exporting ? 'spinner' : ''} />,
      label: 'Export',
      onClick: handleExport,
      tooltip: 'Export current view to Excel',
      variant: 'icon',
      disabled: exporting || loading,
      visible: true,
    },
  ];

  const startIndex = (pagination.currentPage - 1) * pagination.pageSize;

  return (
    <div className="module-master-container">
      {/* Provides the spin keyframes used by LoadingOverlay + icon spinners */}
      <SkeletonStyles />

      {/* Align the filter panel header/footer bars with the grid header/footer */}
      <style>{`
        .rpt-panel-header {
          height: 37px;
          padding: 0 1rem !important;
          border-bottom: 2px solid var(--table-header-border, var(--border-color)) !important;
        }
        .rpt-panel-header .search-panel-title { font-size: 0.8rem; }
        .rpt-footer-bar {
          height: 52px;
          display: flex;
          align-items: center;
          box-sizing: border-box;
          flex-shrink: 0;
        }
        .rpt-pagination-bar {
          padding: 0 1rem;
          border-top: 1px solid var(--border-color);
          background: var(--bg-secondary);
        }
        .rpt-pagination-bar > .pgn-wrap { margin-top: 0 !important; width: 100%; }
        .rpt-filter-actions { height: 52px; padding: 0 1.25rem !important; }
        /* Flatten the grid's bottom corners so it sits flush on the footer bar */
        .rpt-grid-wrapper { border-radius: 0 !important; }
      `}</style>

      <ModuleHeader
        visible={true}
        title={title}
        description={description}
        searchConfig={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: 'Search request no, subject…',
        }}
        filterConfig={{
          onClick: () => setShowFilters((s) => !s),
          isActive: showFilters,
          appliedCount,
        }}
        actions={actions}
      />

      {error && (
        <div
          style={{
            margin: '0.5rem 1.5rem',
            padding: '0.75rem 1rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            color: '#b91c1c',
            fontSize: '0.85rem',
          }}
        >
          {error}
        </div>
      )}

      {/* Applied-filter chips (just above the grid) */}
      {activeChips.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '0.5rem',
            margin: '0 1.5rem 0.25rem 1.5rem',
          }}
        >
          <span style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 600 }}>Active filters:</span>
          {activeChips.map((chip) => (
            <span
              key={chip.key}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.2rem 0.6rem',
                background: '#eff6ff',
                color: '#1d4ed8',
                border: '1px solid #bfdbfe',
                borderRadius: 999,
                fontSize: '0.76rem',
                whiteSpace: 'nowrap',
              }}
            >
              {chip.text}
              <button
                type="button"
                onClick={chip.onRemove}
                title="Remove filter"
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#1d4ed8',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={handleClearAll}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#2563eb',
              cursor: 'pointer',
              fontSize: '0.76rem',
              textDecoration: 'underline',
            }}
          >
            Clear all
          </button>
        </div>
      )}

      {/* Grid + right-side filter drawer */}
      <div className="module-content">
        <div className="data-grid-container" style={{ paddingBottom: 0 }}>
          {/* Progress overlay while the page is loading or an export is running */}
          <LoadingOverlay
            isVisible={loading || exporting}
            message={exporting ? 'Exporting report…' : 'Loading requests…'}
          />

          <div className="data-table-wrapper rpt-grid-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 60, textAlign: 'center' }}>SL NO</th>
                  {columns.map((col) => {
                    const active = col.sortKey && sortBy === col.sortKey;
                    return (
                      <th
                        key={col.label}
                        style={{
                          textAlign: col.align || 'left',
                          cursor: col.sortKey ? 'pointer' : 'default',
                          userSelect: 'none',
                        }}
                        onClick={() => col.sortKey && toggleSort(col.sortKey)}
                      >
                        {col.label}
                        {active ? (sortDescending ? ' ▼' : ' ▲') : ''}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length + 1} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                      No requests match the current filters.
                    </td>
                  </tr>
                )}
                {rows.map((row, idx) => (
                  <tr key={row.requestId}>
                    <td style={{ textAlign: 'center' }}>{startIndex + idx + 1}</td>
                    {columns.map((col) => (
                      <td
                        key={col.label}
                        style={{
                          textAlign: col.align || 'left',
                          whiteSpace: col.nowrap ? 'nowrap' : 'normal',
                        }}
                      >
                        {col.render(row, rowContext)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalItems > 0 && (
            <div className="rpt-footer-bar rpt-pagination-bar">
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                pageSize={pagination.pageSize}
                totalRecords={pagination.totalItems}
                onPageChange={setPage}
                onPageSizeChange={changePageSize}
              />
            </div>
          )}
        </div>

        {/* Right-side filter drawer (matches the app's search-panel pattern) */}
        <div className={`search-panel ${showFilters ? '' : 'collapsed'}`}>
          <div
            className="search-panel-header rpt-panel-header"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <h3 className="search-panel-title">Filters</h3>
            <button
              type="button"
              className="search-panel-close"
              onClick={() => setShowFilters(false)}
              title="Close filters"
              aria-label="Close filters"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '1.5rem',
                height: '1.5rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                background: '#fff',
                color: '#6b7280',
                cursor: 'pointer',
                lineHeight: 1,
                fontSize: '1rem',
              }}
            >
              ×
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <label className="filter-section-title">Request Type</label>
              <div className="form-group">
                <SearchableSelect
                  value={advForm.requestTypeId || ''}
                  onChange={(v) => setAdvForm((f) => ({ ...f, requestTypeId: v }))}
                  options={requestTypes.map((rt: any) => ({ value: rt.id, label: rt.requestTypeName }))}
                  allOptionLabel="All Types"
                  searchPlaceholder="Search type…"
                />
              </div>
            </div>

            {(reportType === 'All' || reportType === 'Decided') && (
              <div className="filter-section">
                <label className="filter-section-title">
                  {reportType === 'Decided' ? 'Decision' : 'Status'}
                </label>
                <div className="form-group">
                  <SearchableSelect
                    value={advForm.status || ''}
                    onChange={(v) => setAdvForm((f) => ({ ...f, status: v }))}
                    options={(reportType === 'Decided' ? ['Approved', 'Rejected'] : ALL_STATUS_OPTIONS).map((s) => ({
                      value: s,
                      label: s,
                    }))}
                    allOptionLabel={reportType === 'Decided' ? 'Approved & Rejected' : 'Any Status'}
                    searchPlaceholder="Search status…"
                  />
                </div>
              </div>
            )}

            <div className="filter-section">
              <label className="filter-section-title">Created From</label>
              <div className="form-group">
                <input
                  type="date"
                  className="form-control"
                  value={advForm.submittedFrom || ''}
                  onChange={(e) => setAdvForm((f) => ({ ...f, submittedFrom: e.target.value }))}
                />
              </div>
            </div>

            <div className="filter-section">
              <label className="filter-section-title">Created To</label>
              <div className="form-group">
                <input
                  type="date"
                  className="form-control"
                  value={advForm.submittedTo || ''}
                  onChange={(e) => setAdvForm((f) => ({ ...f, submittedTo: e.target.value }))}
                />
              </div>
            </div>

            <div className="filter-section">
              <label className="filter-section-title">Min Value</label>
              <div className="form-group">
                <input
                  type="number"
                  className="form-control"
                  value={advForm.minValue ?? ''}
                  onChange={(e) =>
                    setAdvForm((f) => ({ ...f, minValue: e.target.value === '' ? undefined : Number(e.target.value) }))
                  }
                />
              </div>
            </div>

            <div className="filter-section">
              <label className="filter-section-title">Max Value</label>
              <div className="form-group">
                <input
                  type="number"
                  className="form-control"
                  value={advForm.maxValue ?? ''}
                  onChange={(e) =>
                    setAdvForm((f) => ({ ...f, maxValue: e.target.value === '' ? undefined : Number(e.target.value) }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="filter-actions rpt-footer-bar rpt-filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Drill-downs */}
      <ReportRequestDetailModal
        isOpen={!!detailRequestId}
        requestId={detailRequestId}
        onClose={() => setDetailRequestId(null)}
      />
      <PendingDaysBreakdownModal
        isOpen={!!breakdown}
        requestId={breakdown?.id || null}
        requestNumber={breakdown?.requestNumber}
        onClose={() => setBreakdown(null)}
      />
    </div>
  );
};

// ------------------------------------------------------------------------------
// Link-cell style (grid + filter styling comes from the app's CSS classes).
// ------------------------------------------------------------------------------

const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: '#2563eb',
  cursor: 'pointer',
  fontWeight: 600,
  textDecoration: 'underline',
  fontSize: '0.8rem',
};

export default RequestsReportScreen;
